import api from './api';

/**
 * webPush
 * Client helper that subscribes the current browser to Web Push and registers
 * the subscription with the backend. Which notifications a user actually
 * receives is decided server-side based on their role/permissions.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Report the current push state for this browser/device.
 * @returns {Promise<'unsupported'|'denied'|'subscribed'|'unsubscribed'>}
 */
export async function getPushStatus() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? 'subscribed' : 'unsubscribed';
  } catch (error) {
    return 'unsubscribed';
  }
}

/**
 * Subscribe the current browser to push notifications and register it with the
 * backend. Safe to call repeatedly — it reuses an existing subscription.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.requestPermission=false] - Prompt the user if permission
 *        has not yet been decided. Should be triggered by a user gesture.
 * @returns {Promise<boolean>} whether a subscription is active
 */
export async function subscribeToPush({ requestPermission = false } = {}) {
  if (!isPushSupported()) return false;

  // Only prompt when explicitly asked (ideally from a click); otherwise respect
  // the existing choice and stay silent if the user hasn't opted in.
  if (Notification.permission === 'denied') return false;
  if (Notification.permission === 'default') {
    if (!requestPermission) return false;
    const result = await Notification.requestPermission();
    if (result !== 'granted') return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const keyRes = await api.get('/push/vapid-public-key');
      const publicKey = keyRes?.data?.publicKey;
      if (!publicKey) return false;

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }

    await api.post('/push/subscribe', { subscription });
    return true;
  } catch (error) {
    console.warn('Push subscription failed:', error.message);
    return false;
  }
}

/**
 * Remove the current browser's push subscription (call on logout).
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  try {
    // getRegistration() resolves immediately (unlike `ready`, which blocks
    // forever when no service worker is active, e.g. in development).
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
      await subscription.unsubscribe().catch(() => {});
    }
  } catch (error) {
    console.warn('Push unsubscribe failed:', error.message);
  }
}
